import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { AutoCompleteSelectedValue, KeyValuePair } from "../../types/generic";

/**
 * Props for {@link AutoCompleteDropDown}.
 *
 * Shares the same contract as `AutoComplete` (see `components/common/AutoComplete.tsx`).
 */
interface AutoCompleteDropDownProps {
  /** Optional css class names for the text input */
  className?: string;
  /** The id attribute for the text input. Useful when pairing this control with a label */
  inputId?: string;
  /** Optional placeholder attribute to render inside the text input */
  placeholder?: string | undefined;
  /** Current text shown in the input; kept in sync when the value changes externally (e.g. after save). */
  selectedValue: string | undefined;
  /** Whether or not to raise the valueSelected event for custom typed values (i.e. not just ones in the suggestion list) */
  allowCustomTypedValues: boolean;
  /** Whether or not to disable the field */
  disabled?: boolean;
  /** The list of suggestions to pick from */
  valuesList: KeyValuePair<string, string>[];
  /** Fired when the user picks a list item or (if `allowCustomTypedValues`) types a value not in the list. */
  onValueSelected(newValue: AutoCompleteSelectedValue | null): void;
  handleCustomTypedTitle?: (customTitle: string) => void;
  /** Whether to display the key or the value of the selected item in the text input field once a selection has been made */
  suggestionPropertyToDisplay?: "KEY" | "VALUE";
  /** An optional custom method for the HTML to render for each suggestion */
  customSuggestionRender?(
    suggestion: KeyValuePair<string, string>
  ): React.ReactNode;
  /** If provided, instead of the standard searching for suggestions based on the input list provided, the logic for searching can be handled outside this component */
  customSearchFunction?:
    | undefined
    | ((searchValue: string) => KeyValuePair<string, string>[]);
  /** Async search (e.g. API-backed suggestions). Takes precedence over customSearchFunction when set. */
  asyncSearchFunction?: (
    searchValue: string
  ) => Promise<KeyValuePair<string, string>[]>;
  /** Called when the input loses focus */
  onInputBlur?: () => void;
}

interface DropdownLayout {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  openUpward: boolean;
}

const DROPDOWN_PREFERRED_MAX_HEIGHT_PX = 208;
const DROPDOWN_MIN_MAX_HEIGHT_PX = 96;
const DROPDOWN_VIEWPORT_PADDING_PX = 16;

const sortSuggestionsAlphabetically = (
  items: KeyValuePair<string, string>[]
): KeyValuePair<string, string>[] =>
  [...items].sort((a, b) =>
    a.value.localeCompare(b.value, undefined, { sensitivity: "base" })
  );

const computeDropdownLayout = (input: HTMLElement): DropdownLayout => {
  const rect = input.getBoundingClientRect();
  const padding = DROPDOWN_VIEWPORT_PADDING_PX;

  const spaceBelow = window.innerHeight - rect.bottom - padding;
  const spaceAbove = rect.top - padding;
  const openUpward =
    spaceBelow < DROPDOWN_MIN_MAX_HEIGHT_PX && spaceAbove > spaceBelow;

  const available = Math.floor(openUpward ? spaceAbove : spaceBelow);
  const maxHeight = Math.max(
    DROPDOWN_MIN_MAX_HEIGHT_PX,
    Math.min(DROPDOWN_PREFERRED_MAX_HEIGHT_PX, available)
  );

  const top = openUpward
    ? Math.max(padding, rect.top - maxHeight)
    : rect.bottom;

  return {
    top,
    left: rect.left,
    width: rect.width,
    maxHeight,
    openUpward,
  };
};

const dropdownLayoutsEqual = (a: DropdownLayout, b: DropdownLayout) =>
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.maxHeight === b.maxHeight &&
  a.openUpward === b.openUpward;

/**
 * Searchable text input with a floating suggestion list (dropdown overlay).
 *
 * **Behaviour**
 * - On focus or while typing, opens a fixed-position panel under the input (overlaps content below; does not grow the layout).
 * - **Empty input:** shows all available options, sorted A–Z (`valuesList`, `customSearchFunction("")`, or `asyncSearchFunction("")`).
 * - **Non-empty input:** filters suggestions as the user types (prefix match on a static list; custom/async handlers receive the query).
 * - The panel scrolls when needed, clamps to the viewport, and can open upward when there is little space below.
 * - Keyboard: Arrow Up/Down to move highlight, Enter to select, Escape to close. Mouse: click an item to select.
 * - Chevron on the right toggles the full suggestion list (empty query, same as an empty input).
 *   Results are only filtered when the user types in the input.
 *
 * **When to use this instead of `AutoComplete`**
 * - Use **AutoCompleteDropDown** when users should **browse or pick without typing first** — e.g. choosing from known
 *   custom-field values, location names, or API-backed distinct values where an empty search still returns options.
 * - Use **AutoComplete** for classic **typeahead**: suggestions appear only after the user has entered text (e.g. catch-up
 *   form title search). An empty query shows no list in `AutoComplete`.
 *
 * Both components accept the same props and return {@link AutoCompleteSelectedValue} from `onValueSelected`.
 */
const AutoCompleteDropDown = ({
  inputId = "",
  selectedValue = "",
  placeholder,
  valuesList,
  onValueSelected,
  handleCustomTypedTitle,
  className = "",
  allowCustomTypedValues = false,
  suggestionPropertyToDisplay = "VALUE",
  customSuggestionRender,
  customSearchFunction,
  asyncSearchFunction,
  onInputBlur,
  disabled = false,
}: AutoCompleteDropDownProps) => {
  const [displayText, setDisplayText] = useState<string>(selectedValue ?? "");
  const [suggestions, setSuggestions] = useState<
    KeyValuePair<string, string>[]
  >([]);
  const [isListOpen, setIsListOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownLayout, setDropdownLayout] = useState<DropdownLayout | null>(
    null
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchRequestIdRef = useRef(0);

  useEffect(() => {
    setDisplayText(selectedValue ?? "");
  }, [selectedValue]);

  const updateDropdownLayout = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const nextLayout = computeDropdownLayout(containerRef.current);
    setDropdownLayout((current) =>
      current && dropdownLayoutsEqual(current, nextLayout) ? current : nextLayout
    );
  }, []);

  const getSuggestions = useCallback(
    (value: string) => {
      const inputValue = value.trim().toLowerCase();
      const inputLength = inputValue.length;

      if (inputLength === 0) {
        if (customSearchFunction) {
          return sortSuggestionsAlphabetically(customSearchFunction(""));
        }
        return sortSuggestionsAlphabetically(valuesList);
      }

      if (customSearchFunction) {
        return sortSuggestionsAlphabetically(customSearchFunction(value));
      }

      return sortSuggestionsAlphabetically(
        valuesList.filter(
          (kvp) =>
            kvp.value.toLowerCase().slice(0, inputLength) === inputValue
        )
      );
    },
    [customSearchFunction, valuesList]
  );

  const fetchSuggestions = useCallback(
    (value: string) => {
      if (asyncSearchFunction) {
        const requestId = ++searchRequestIdRef.current;
        asyncSearchFunction(value)
          .then((results) => {
            if (requestId === searchRequestIdRef.current) {
              const sorted = sortSuggestionsAlphabetically(results);
              setSuggestions(sorted);
              setIsListOpen(sorted.length > 0);
              setHighlightedIndex(-1);
            }
          })
          .catch(() => {
            if (requestId === searchRequestIdRef.current) {
              setSuggestions([]);
              setIsListOpen(false);
            }
          });
        return;
      }

      const next = getSuggestions(value);
      setSuggestions(next);
      setIsListOpen(next.length > 0);
      setHighlightedIndex(-1);
    },
    [asyncSearchFunction, getSuggestions]
  );

  useLayoutEffect(() => {
    if (!isListOpen || suggestions.length === 0) {
      setDropdownLayout(null);
      return;
    }

    updateDropdownLayout();
  }, [isListOpen, suggestions.length, updateDropdownLayout]);

  useEffect(() => {
    if (!isListOpen) {
      return;
    }

    const onReposition = () => updateDropdownLayout();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);

    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [isListOpen, updateDropdownLayout]);

  useEffect(() => {
    const onDocMouseDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsListOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const emitCustomValue = (newValue: string) => {
    if (handleCustomTypedTitle) {
      handleCustomTypedTitle(newValue);
    }

    onValueSelected({
      displayText: newValue,
      isCustomValue: true,
      key: null,
    });
  };

  const applySuggestionPick = (suggestion: KeyValuePair<string, string>) => {
    const displayValue =
      suggestionPropertyToDisplay === "VALUE"
        ? suggestion.value
        : suggestion.key;

    setDisplayText(displayValue);
    setIsListOpen(false);
    setHighlightedIndex(-1);

    onValueSelected({
      displayText: suggestion.value,
      isCustomValue: false,
      key: suggestion.key,
    });
  };

  const handleInputChange = (newValue: string) => {
    setDisplayText(newValue);
    fetchSuggestions(newValue);

    if (allowCustomTypedValues) {
      emitCustomValue(newValue);
    }
  };

  const handleInputFocus = () => {
    fetchSuggestions(displayText);
  };

  const handleInputBlur = () => {
    setIsListOpen(false);
    setHighlightedIndex(-1);
    onInputBlur?.();
  };

  const toggleList = () => {
    if (disabled) {
      return;
    }

    if (isListOpen) {
      setIsListOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    // Chevron always opens the full list; typing alone narrows results.
    fetchSuggestions("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isListOpen || suggestions.length === 0) {
      if (event.key === "Escape") {
        setIsListOpen(false);
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((index) => {
        if (index < 0) {
          return 0;
        }
        return Math.min(index + 1, suggestions.length - 1);
      });
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
      return;
    }

    if (event.key === "Enter" && highlightedIndex >= 0) {
      event.preventDefault();
      applySuggestionPick(suggestions[highlightedIndex]);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setIsListOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const showDropdown =
    isListOpen && suggestions.length > 0 && dropdownLayout !== null;

  return (
    <div ref={containerRef} className="relative w-full min-w-0">
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        className={cx(className, !disabled && "pr-8")}
        value={displayText}
        onChange={(event) => handleInputChange(event.target.value)}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
      />

      {!disabled && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={isListOpen ? "Hide suggestions" : "Show suggestions"}
          aria-expanded={isListOpen}
          aria-haspopup="listbox"
          className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-400 hover:text-gray-600"
          onMouseDown={(event) => event.preventDefault()}
          onClick={toggleList}
        >
          <FontAwesomeIcon
            icon={isListOpen ? faChevronUp : faChevronDown}
            size="sm"
          />
        </button>
      )}

      {showDropdown && dropdownLayout && (
        <ul
          role="listbox"
          className={cx(
            "fixed z-[100] overflow-y-auto overflow-x-hidden rounded-md border border-gray-300 bg-white shadow-lg m-0 list-none p-0",
            dropdownLayout.openUpward && "rounded-b-none",
            !dropdownLayout.openUpward && "rounded-t-none"
          )}
          style={{
            top: dropdownLayout.top,
            left: dropdownLayout.left,
            width: dropdownLayout.width,
            maxHeight: dropdownLayout.maxHeight,
          }}
        >
          {suggestions.map((suggestion, index) => (
            <li key={`${suggestion.key}-${suggestion.value}`} role="option">
              <button
                type="button"
                className={cx(
                  "w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-100",
                  index === highlightedIndex && "bg-gray-100"
                )}
                onMouseDown={(event) => {
                  event.preventDefault();
                  applySuggestionPick(suggestion);
                }}
              >
                {customSuggestionRender
                  ? customSuggestionRender(suggestion)
                  : suggestion.value}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AutoCompleteDropDown;
